import { Api } from "@/core/services/api.service";
import { CategoryAdapter } from "@/core/models/category";
import { QueryUrlBuilderService } from "@/core/services/queryUrlBuilder.service";
import { SORT_ORDERS } from "@/core/constants/sortOrders";
import treeSelectUtils from "@/core/utils/treeSelect.utils";
import useCategoryStore from "@/core/store/catalog/categoryStore";

/**
 * @param {boolean} pagination
 * @param options
 * @param format
 * @returns {Promise<{totalItems, items: *}>}
 */
const fetchCategoriesList = async (pagination = false, options = {}, format = "json") => {
  const urlBuilder = new QueryUrlBuilderService("categories")
    .withPagination(options.page, options.perPage, pagination)
    .withSort(options.sort, options.order);

  if (!options.count) {
    urlBuilder.withoutCount();
  }

  ["sort", "order", "page", "perPage", "count"].forEach((e) => delete options[e]);
  urlBuilder.withFilters(options);

  const acceptHeader = "jsonld" === format ? "application/ld+json" : "application/json";

  const response = await Api.new().get(urlBuilder.get(), {
    headers: { accept: acceptHeader },
  });

  const data = "jsonld" === format ? response.data["hydra:member"] : response.data;
  const categories = data.map((categoryApi) => CategoryAdapter.adapt(categoryApi));

  return {
    items: categories,
    totalItems: "jsonld" === format ? response.data["hydra:totalItems"] : categories.length,
  };
};

const fetchCategoriesTree = async () => {
  const response = await Api.new().get("categories/tree");
  return response.data;
};

const fetchCategoriesTreeWithProducts = async (sortOrderByLabel = null) => {
  const urlBuilder = new QueryUrlBuilderService("categories/with-products");

  if (sortOrderByLabel && Object.values(SORT_ORDERS).includes(sortOrderByLabel)) {
    urlBuilder.withSort("label", sortOrderByLabel);
  }
  const response = await Api.new().get(urlBuilder.get());
  return response.data.map((categoryApi) => CategoryAdapter.adapt(categoryApi));
};

const fetchProductCategoryTree = async (productId) => {
  const response = await Api.new().get(`products/${productId}/category_tree`);
  return response.data;
};

const fetchCategoriesTreeById = async (id) => {
  return Api.new().get(`categories/tree/${id}`);
};

/**
 * @param {string} categoryId
 * @returns {Promise<Category>}
 */
const fetchCategoryById = async (categoryId) => {
  const response = await Api.new().get(`categories/${categoryId}`);
  const categoryApi = response.data;
  return CategoryAdapter.adapt(categoryApi);
};

const createCategory = async (newCategoryData) => {
  const { clearCategoryTree } = useCategoryStore();
  clearCategoryTree();

  const response = await Api.new().post("categories", newCategoryData);

  const categoryApi = response.data;
  return CategoryAdapter.adapt(categoryApi);
};

const updateCategory = async (categoryId, updateData) => {
  const { clearCategoryTree } = useCategoryStore();
  clearCategoryTree();

  return Api.new().patch(`categories/${categoryId}`, updateData, {
    headers: { "Content-Type": "application/merge-patch+json" },
  });
};

const addDispatchCircuitRule = async (categoryId, dcrId) => {
  return Api.new().post(`categories/${categoryId}/circuits/${dcrId}`, null, {
    headers: { "Content-Type": "application/merge-patch+json" },
  });
};

const removeDispatchCircuitRule = async (categoryId, dcrId) => {
  return Api.new().delete(`categories/${categoryId}/circuits/${dcrId}`, null, {
    headers: { "Content-Type": "application/merge-patch+json" },
  });
};

const loadChildren = async (action, parentNode) => {
  const response = await Api.new().get(`categories/tree/${parentNode.id}`);

  const children = treeSelectUtils.setEmptyChildrenAsNull(response.data.children);

  parentNode.children = children;
};

const checkpointsToArchive = (categoryId) => {
  return Api.new().get(`categories/${categoryId}/checkpoints-to-archive`);
};

/**
 * @param {string[]} categoryIds
 */
const getChannels = async (categoryIds) => {
  const { data } = await Api.new().get(`dispatch-circuit-rules`);

  return data.reduce((channelsByCategoryId, dispatchCircuitRule) => {
    dispatchCircuitRule.categoryIds
      .filter((categoryId) => categoryIds.includes(categoryId))
      .forEach((categoryId) => {
        let channels = [];
        if (channelsByCategoryId.has(categoryId)) {
          channels = channelsByCategoryId.get(categoryId);
        }
        if (!channels.includes(dispatchCircuitRule.circuit.channel)) {
          channels.push(dispatchCircuitRule.circuit.channel);
        }
        channelsByCategoryId.set(categoryId, channels);
      });

    return channelsByCategoryId;
  }, new Map());
};

const getConnectedCategoriesTree = async () => {
  const { data } = await Api.new().get("categories/connected-tree");

  return data;
};

const fetchRootCategory = async () => {
  const { data } = await Api.new().get("/internal/categories/root");

  return data;
};

const fetchCategory = async (categoryId) => {
  const { data } = await Api.new().get(`/internal/categories/${categoryId}`);

  return data;
};

const fetchCategoryAncestry = async (categoryId) => {
  const { data } = await Api.new().get(`/internal/categories/${categoryId}/ancestry`);

  return data;
};

export default {
  fetchCategoriesList,
  fetchProductCategoryTree,
  fetchCategoriesTree,
  fetchCategoriesTreeWithProducts,
  fetchCategoriesTreeById,
  fetchCategoryById,
  createCategory,
  updateCategory,
  addDispatchCircuitRule,
  removeDispatchCircuitRule,
  loadChildren,
  checkpointsToArchive,
  getChannels,
  getConnectedCategoriesTree,
  fetchRootCategory,
  fetchCategory,
  fetchCategoryAncestry,
};
